/**
 * Çoklu dil (i18n) katmanı — Türkçe (tr) ve İngilizce (en) destekler.
 * Dil tercihi localStorage'da 'biyolink_lang' anahtarıyla saklanır (varsayılan: 'tr').
 *
 * Koda gömülü Türkçe string YAZMA — her metin tr.json ve en.json içinde olmalı.
 */

import { create } from 'zustand';
import tr from './tr.json';
import en from './en.json';

export type Language = 'tr' | 'en';
type Messages = typeof tr;

const STORAGE_KEY = 'biyolink_lang';

/** "home.heroTitle" gibi noktalı anahtarların birleşimi. */
type LeafKeys<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${LeafKeys<T[K]>}`;
}[keyof T & string];

export type TranslationKey = LeafKeys<Messages>;

const dictionaries: Record<Language, Messages> = { tr, en };

/*
 * localStorage erişimi try/catch içinde: gizli sekmede ya da site verisi engelliyken
 * okuma da yazma da istisna fırlatıyor ve uygulama açılışta patlıyordu.
 */
function getInitialLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'tr' || saved === 'en') return saved;
  } catch {
    // Depolama kapalı: varsayılan dille devam.
  }
  return 'tr';
}

interface LanguageState {
  lang: Language;
  setLanguage: (lang: Language) => void;
}

export const useLanguageStore = create<LanguageState>((set) => ({
  lang: getInitialLanguage(),
  setLanguage: (lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Depolama kapalı: tercih bu oturumda geçerli, kalıcı olmuyor.
    }
    document.documentElement.lang = lang;
    set({ lang });
  },
}));

// Sayfa yüklendiğinde html lang özniteliğini güncelle
document.documentElement.lang = getInitialLanguage();

function resolve(key: string, lang: Language): string | undefined {
  const segments = key.split('.');
  const primaryDict = dictionaries[lang];
  const fallbackDict = dictionaries[lang === 'tr' ? 'en' : 'tr'];

  let current: unknown = primaryDict;
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      current = undefined;
      break;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  if (typeof current === 'string') return current;

  // İkincil sözlükten yedek (fallback)
  let fallbackCurrent: unknown = fallbackDict;
  for (const segment of segments) {
    if (typeof fallbackCurrent !== 'object' || fallbackCurrent === null) return undefined;
    fallbackCurrent = (fallbackCurrent as Record<string, unknown>)[segment];
  }

  return typeof fallbackCurrent === 'string' ? fallbackCurrent : undefined;
}

/**
 * Aktif dilde çeviri metnini döndürür. `{isim}` biçimindeki yer tutucular `vars` ile doldurulur.
 *
 * @example t('home.heroTitle')
 * @example t('listing.priceWithUnit', { price: '1.250', unit: 'ton' })
 */
export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  const currentLang = useLanguageStore.getState().lang;
  const template = resolve(key, currentLang);

  if (template === undefined) {
    console.warn(`[i18n] Eksik çeviri anahtarı: ${key}`);
    return key;
  }

  if (!vars) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

/** Aktif dilin Intl yerel kodu — tarih/sayı biçimlendiricileri için. */
export function currentLocale(): string {
  return useLanguageStore.getState().lang === 'en' ? 'en-GB' : 'tr-TR';
}

/**
 * Enum değerini etiketine çevirir: `tEnum('units', 'TON')` → "ton" (veya "ton" in EN).
 * Bilinmeyen değerde ham değeri döndürür.
 */
export function tEnum<G extends keyof Messages>(group: G, value: string): string {
  const currentLang = useLanguageStore.getState().lang;
  const groupMessages = dictionaries[currentLang][group];

  if (typeof groupMessages === 'object' && groupMessages !== null && value in groupMessages) {
    return (groupMessages as Record<string, string>)[value] ?? value;
  }

  // Fallback to Turkish if missing in current lang
  const fallbackGroup = dictionaries.tr[group];
  if (typeof fallbackGroup === 'object' && fallbackGroup !== null && value in fallbackGroup) {
    return (fallbackGroup as Record<string, string>)[value] ?? value;
  }

  return value;
}

/** React bileşenlerinde dil durumu ve değiştirme fonksiyonuna erişim hook'u */
export function useLanguage() {
  const lang = useLanguageStore((state) => state.lang);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  return { lang, setLanguage, t, tEnum };
}
